import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Check, FileText } from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";
import { SectionCard, Button as DashButton } from "../components/primitives";
import { useApi } from "../../../hooks/useApi";
import { DEFAULT_WS_PRESET, WS_PRESETS } from "./workSummaryNew/reportConfig";

function Radio({ on, t }) {
  return (
    <span aria-hidden style={{
      width: 18, height: 18, borderRadius: 99, flex: "none",
      border: `2px solid ${on ? t.c.primary : t.c.borderStrong || t.c.border}`,
      background: on ? t.c.primary : "transparent",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
    }}>
      {on && <Check size={11} strokeWidth={3} color="#fff" />}
    </span>
  );
}

export default function WorkSummaryReportSettings() {
  const t = useDashTheme();
  const api = useApi();
  const [preset, setPreset] = useState(DEFAULT_WS_PRESET);
  const [saved, setSaved] = useState(DEFAULT_WS_PRESET);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const dirty = preset !== saved;
  const current = WS_PRESETS.find((p) => p.id === preset) || WS_PRESETS[0];
  const others = WS_PRESETS.filter((p) => !p.isDefault);
  const def = WS_PRESETS.find((p) => p.isDefault) || WS_PRESETS[0];

  useEffect(() => {
    let dead = false;
    api.get("/api/v1/organizations/work-summary-report")
      .then((r) => {
        if (dead) return;
        const id = r.data?.data?.report?.preset || DEFAULT_WS_PRESET;
        setPreset(id);
        setSaved(id);
      })
      .catch(() => { if (!dead) toast.error("Could not load Work Summary settings"); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [api]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/api/v1/organizations/work-summary-report", { preset });
      const id = data?.data?.report?.preset || preset;
      setPreset(id);
      setSaved(id);
      toast.success("Saved — every Work Summary uses this now");
    } catch (_e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ color: t.c.textSecondary, fontSize: 13, padding: 8 }}>Loading…</div>;
  }

  const card = (p, { featured } = {}) => {
    const on = preset === p.id;
    return (
      <button
        key={p.id}
        type="button"
        onClick={() => setPreset(p.id)}
        style={{
          textAlign: "left", width: "100%", cursor: "pointer",
          padding: featured ? "16px 18px" : "13px 14px",
          borderRadius: 14,
          border: `1.5px solid ${on ? t.c.primary : t.c.border}`,
          background: on ? `color-mix(in srgb, ${t.c.primary} 9%, ${t.c.surface})` : t.c.surface,
          color: t.c.text,
          display: "flex", gap: 12, alignItems: "flex-start",
          boxShadow: featured ? t.shadow?.sm : "none",
        }}
      >
        <Radio on={on} t={t} />
        <div style={{ minWidth: 0, flex: 1, display: "grid", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: featured ? 15.5 : 14, lineHeight: 1.25 }}>{p.title}</span>
            {p.isDefault && (
              <span style={{
                fontSize: 10.5, fontWeight: 750, letterSpacing: "0.06em", textTransform: "uppercase",
                color: "#fff", background: t.c.primary, borderRadius: 99, padding: "2px 7px",
              }}>Default</span>
            )}
          </div>
          <div style={{ fontSize: 12, fontWeight: 650, color: t.c.textTertiary }}>{p.audience}</div>
          <div style={{ fontSize: 13, color: t.c.textSecondary, lineHeight: 1.45 }}>{p.blurb}</div>
        </div>
      </button>
    );
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "12px 16px", borderRadius: 14, border: `1px solid ${t.c.border}`,
        background: t.c.surfaceMuted || t.c.surface,
      }}>
        <FileText size={16} color={t.c.primary} />
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 12, fontWeight: 650, color: t.c.textTertiary, letterSpacing: "0.04em", textTransform: "uppercase" }}>In use</div>
          <div style={{ fontWeight: 700, fontSize: 14.5, color: t.c.text }}>{current.title}</div>
        </div>
        <DashButton size="sm" variant="primary" disabled={saving || !dirty} onClick={save}>
          {saving ? "Saving…" : dirty ? "Save for everyone" : "Saved"}
        </DashButton>
      </div>

      <SectionCard
        title="Choose the report"
        subtitle="One layout for every person's Work Summary. Only admins can change this."
        icon={<FileText size={14} />}
      >
        <div style={{ padding: 16, display: "grid", gap: 14 }}>
          {card(def, { featured: true })}
          <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: t.c.textTertiary, padding: "4px 2px 0" }}>
            Other layouts
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8 }}>
            {others.map((p) => card(p))}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
